import React, { useState, startTransition } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { getColourFromString } from '../../utils/ColourNameGenerator';
import { ListItemChip } from '../../utils/ListItemChip';

const WorkflowMultiSelect = ({
  selectedWorkflows = [],
  onWorkflowChange,
  allWorkflows = [],
  loading = false,
  disabled = false,
  error = false,
  placeholder = 'Select workflows',
  showSelectAllButtons = true,
  helperText,
  size = 'small',
  variant = 'outlined',
  sx = {},
}) => {
  const [selecting, setSelecting] = useState(false);

  const handleSelectAll = () => {
    if (!showSelectAllButtons) return;

    setSelecting(true);
    startTransition(() => {
      onWorkflowChange(allWorkflows);
      setSelecting(false);
    });
  };

  const handleDeselectAll = () => {
    if (!showSelectAllButtons) return;
    onWorkflowChange([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Loading workflows...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {showSelectAllButtons && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item>
            <Button
              size="small"
              variant="text"
              onClick={handleSelectAll}
              disabled={disabled || allWorkflows.length === 0 || selecting}
              sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
            >
              {selecting ? (
                <>
                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                  Selecting...
                </>
              ) : (
                'Select All'
              )}
            </Button>
          </Grid>
          <Grid item>
            <Button
              size="small"
              variant="text"
              onClick={handleDeselectAll}
              disabled={disabled || selectedWorkflows.length === 0}
              sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
            >
              Deselect All
            </Button>
          </Grid>
        </Grid>
      )}

      <Autocomplete
        options={allWorkflows}
        disableCloseOnSelect
        disabled={disabled}
        renderOption={(props, option) => (
          <li {...props} key={option.projectId}>
            {option.projectName}
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, idx) => (
            <ListItemChip
              {...getTagProps({ index: idx })}
              key={option.projectId}
              label={option.projectName}
              colour={getColourFromString(option.projectName)}
            />
          ))
        }
        size={size}
        multiple
        openOnFocus
        getOptionLabel={option => option.projectName || ''}
        isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
        value={selectedWorkflows}
        onChange={(event, newValue) => onWorkflowChange(newValue)}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={selectedWorkflows.length === 0 ? placeholder : ''}
            size={size}
            variant={variant}
            error={error}
            helperText={error ? helperText : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              ...sx,
            }}
          />
        )}
      />
    </Box>
  );
};

export default WorkflowMultiSelect;
